import { useState } from "react";
import { ShieldCheck, AlertTriangle } from "lucide-react";
import { useTranslation, Trans } from "react-i18next";

const STORAGE_KEY = "nexus_age_confirmed_v1";

function readInitial() {
    try {
        if (typeof window === "undefined") return "asking";
        return localStorage.getItem(STORAGE_KEY) === "true" ? "confirmed" : "asking";
    } catch {
        return "asking";
    }
}

export default function AgeGate() {
    const { t } = useTranslation();
    // 'asking' | 'confirmed' | 'denied'
    const [status, setStatus] = useState(readInitial);

    const confirmAdult = () => {
        try {
            localStorage.setItem(STORAGE_KEY, "true");
        } catch (err) {
            console.error("[AgeGate] failed to persist confirmation:", err);
        }
        setStatus("confirmed");
    };

    if (status === "confirmed") return null;

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-white/85 backdrop-blur-md"
            role="dialog"
            aria-modal="true"
            aria-labelledby="age-gate-title"
            data-testid="age-gate"
        >
            <div className="relative w-full max-w-lg bg-white border border-[#00aeef]/30 rounded-2xl overflow-hidden shadow-2xl">
                <div
                    className="absolute -top-20 -right-20 w-64 h-64 rounded-full pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(circle, rgba(10, 10, 10, 0.22) 0%, transparent 65%)",
                    }}
                />
                <div className="relative flex items-center gap-3 px-6 lg:px-8 pt-6">
                    <div className="relative w-7 h-7">
                        <div className="absolute inset-0 rounded-lg border border-[#00aeef]" />
                        <div className="absolute inset-1.5 rounded-lg bg-[#f8f8f8]/30" />
                    </div>
                    <div className="font-display font-extrabold uppercase tracking-[0.18em] text-sm">
                        Nexus<span className="text-[#0077b6]">.</span>Peptides
                    </div>
                </div>

                {status === "asking" && (
                    <div className="relative px-6 lg:px-8 pb-8 pt-6">
                        <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6] mb-3 flex items-center gap-2">
                            <ShieldCheck className="w-3.5 h-3.5" /> {t("ageGate.eyebrow")}
                        </div>
                        <h2
                            id="age-gate-title"
                            className="font-display text-3xl lg:text-4xl font-extrabold uppercase tracking-tighter mb-4"
                        >
                            {t("ageGate.titleLine1")}
                            <br />
                            {t("ageGate.titleLine2")}
                        </h2>
                        <p className="text-neutral-600 leading-relaxed text-sm">
                            {t("ageGate.body")}
                        </p>

                        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <button
                                type="button"
                                onClick={confirmAdult}
                                className="bg-[#0089c8] hover:bg-[#0077b6] rounded-lg text-white font-bold uppercase tracking-[0.18em] text-xs py-4 transition-colors"
                                data-testid="age-gate-yes"
                            >
                                {t("ageGate.yes")}
                            </button>
                            <button
                                type="button"
                                onClick={() => setStatus("denied")}
                                className="border border-neutral-300 hover:border-amber-400 hover:text-amber-700 rounded-lg text-neutral-700 font-semibold uppercase tracking-[0.18em] text-xs py-4 transition-colors"
                                data-testid="age-gate-no"
                            >
                                {t("ageGate.no")}
                            </button>
                        </div>

                        <p className="mt-5 text-[10px] uppercase tracking-[0.22em] text-neutral-500">
                            {t("ageGate.storedNote")}
                        </p>
                    </div>
                )}

                {status === "denied" && (
                    <div
                        className="relative px-6 lg:px-8 pb-8 pt-6"
                        data-testid="age-gate-denied"
                    >
                        <div className="text-[10px] uppercase tracking-[0.28em] text-amber-400 mb-3 flex items-center gap-2">
                            <AlertTriangle className="w-3.5 h-3.5" /> {t("ageGate.deniedEyebrow")}
                        </div>
                        <h2 className="font-display text-3xl lg:text-4xl font-extrabold uppercase tracking-tighter mb-5">
                            {t("ageGate.deniedTitleLine1")}
                            <br />
                            {t("ageGate.deniedTitleLine2")}
                        </h2>
                        <div className="border border-amber-500/30 bg-amber-500/[0.06] rounded-lg p-5 text-amber-900/90 text-sm leading-relaxed">
                            {t("ageGate.deniedBody1")}
                            <span className="font-bold">{t("ageGate.deniedBodyEmphasis")}</span>
                            {t("ageGate.deniedBody2")}
                        </div>
                        <p className="mt-6 text-xs text-neutral-500 leading-relaxed">
                            {t("ageGate.retryPrefix")}
                            <button
                                type="button"
                                onClick={() => setStatus("asking")}
                                className="text-[#0077b6] hover:underline"
                                data-testid="age-gate-retry"
                            >
                                {t("ageGate.retryLink")}
                            </button>
                            {t("ageGate.retrySuffix")}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
